import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { _post } from '../../../utils/network';

import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb.component';
import FormField from '../../../components/Form/FormField/FormField.component';
import LoadingButton from '../../../components/LoadingButton/LoadingButton.component';
import ListTable from '../../../components/List/ListTable/ListTable.component';
import Pagination from '../../../components/List/Pagination/Pagination.component';

const activationToBoolean = value => value === 'active' ? true : value === 'deactive' ? false : null;

const CourseCategoriesList = () => {
	const { t } = useTranslation();
	const [courseCategories, setCourseCategories] = useState([]);
	const [totalCount, setTotalCount] = useState(0);
	const [searchButtonIsLoading, setSearchButtonIsLoading] = useState(false);
	const [searchParams, setSearchParams] = useState({
		title: null,
		is_active: null,
		sort: null,
		page: 1,
		count: 10
	});

	const breadcrumbItems = [
		{ title: t('dashboard'), link: '/dashboard' },
		{ title: t('course_categories'), link: '/course-categories-list' },
		{ title: t('list') }
	];
	const activationsList = [
		{ key: t('active'), value: 'active' },
		{ key: t('deactive'), value: 'deactive' }
	];
	const sortsList = [
		{ key: t('newest'), value: 'date-desc' },
		{ key: t('oldest'), value: 'date-asc' }
	];

	const courseCategoriesListData = useMemo(() => ({
		head: [t('title'), t('status'), t('operation')],
		body: courseCategories.map(courseCategory => [
			courseCategory.title,
			<span className={`badge rounded-pill p-2 badge-subtle-${courseCategory.is_active ? 'success' : 'secondary'}`}>
				{courseCategory.is_active ? t('active') : t('deactive')}
			</span>,
			<Link className='btn btn-secondary btn-sm p-1' to={`/course-categories-edit/${courseCategory.id}`}>
				<i className="fa fa-edit"></i> {t('edit')}
			</Link>
		]),
		directions: ['', '', 'ltr']
	}), [courseCategories, t]);

	const getCourseCategories = async () => {
		setSearchButtonIsLoading(true);
		const response = await _post('/course_categories/admin/getCourseCategories', searchParams);
		setSearchButtonIsLoading(false);
		switch(response.code){
			case 200:
				setTotalCount(response.data.course_categories_count);
				setCourseCategories(response.data.course_categories);
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};

	const filterCourseCategories = event => {
		event.preventDefault();
		if(searchButtonIsLoading)
			return;
		const formData = new FormData(event.target);
		setSearchParams({
			...searchParams,
			title: formData.get('searchCourseCategoryTitle')?.trim() || null,
			is_active: activationToBoolean(formData.get('searchCourseCategoryIsActive')),
			sort: formData.get('searchCourseCategorySort') || null,
			page: 1
		});
	};

	useEffect(() => {
		getCourseCategories();
	}, [searchParams]);

	return (
		<>
			<Breadcrumb items={breadcrumbItems} />
			<div className="card mb-3">
				<div className="card-header">
					<h5 className="mb-0">{t('filter')} {t('course_categories')}</h5>
				</div>
				<div className="card-body bg-light">
					<form onSubmit={filterCourseCategories}>
						<div className="row gx-2">
							<FormField fieldKey="searchCourseCategoryTitle" title={t('title')} isSearch />
							<FormField type="select" fieldKey="searchCourseCategoryIsActive" title={t('status')} selectItems={activationsList} isSearch />
							<FormField type="select" fieldKey="searchCourseCategorySort" title={t('sorting')} selectItems={sortsList} isSearch required />
						</div>
						<div>
							<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="search" isLoading={searchButtonIsLoading}>
								<i className="fa fa-search"></i> {t('search')}
							</LoadingButton>
						</div>
					</form>
				</div>
			</div>
			<ListTable title={t('course_categories')} pageNumber={searchParams.page} pageCounter={searchParams.count} totalCount={totalCount} data={courseCategoriesListData} />
			<Pagination pagesCount={Math.ceil(totalCount / searchParams.count)} action={page => setSearchParams({ ...searchParams, page })} />
		</>
	);
};

export default CourseCategoriesList;
